import type { CatalogProduct, CatalogProductVariant } from '../../types'
import {
  getVariantAvailableStock,
  getVariantKey,
  getVariantLabel,
  getVariantSummary,
} from './model/productVariants'

type Props = {
  productData: CatalogProduct
  variants: CatalogProductVariant[]
  selectedVariantKey: string
  onSelectVariant: (variantKey: string) => void
}

export const ProductVariantSelector = ({
  productData,
  variants,
  selectedVariantKey,
  onSelectVariant,
}: Props) => {
  if (variants.length === 0) return null

  const renderVariantContent = (variant: CatalogProductVariant, index: number) => {
    const availableStock = getVariantAvailableStock(productData, variant)
    const label = getVariantLabel(variant, index)
    const summary = getVariantSummary(variant)

    return (
      <>
        <span className="block text-lg font-black text-slate-950">{label}</span>
        {summary ? <span className="mt-1 block text-sm font-medium text-slate-600">{summary}</span> : null}
        <span className="mt-3 flex items-center gap-2 border-t border-slate-200 pt-3 text-sm font-semibold text-slate-600">
          <span
            className={`h-2 w-2 rounded-full ${availableStock > 0 ? 'bg-emerald-500' : 'bg-rose-500'}`}
            aria-hidden="true"
          />
          {availableStock > 0 ? `${availableStock} disponibili` : 'Non disponibile'}
        </span>
      </>
    )
  }

  return (
    <section aria-labelledby="product-variant-selector-title" className="space-y-3">
      <div>
        <h2 id="product-variant-selector-title" className="text-sm font-black uppercase tracking-wide text-slate-950">
          Configurazione
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          {variants.length > 1
            ? 'Scegli la configurazione prima di aggiungere il prodotto al carrello.'
            : 'Configurazione disponibile per questo prodotto.'}
        </p>
      </div>
      <div className={variants.length > 1 ? 'grid gap-3 xl:grid-cols-2' : undefined}>
        {variants.map((variant, index) => {
          const key = getVariantKey(variant, index)
          const selected = key === selectedVariantKey

          if (variants.length === 1) {
            return (
              <div key={key} className="border-y border-slate-200 py-4">
                {renderVariantContent(variant, index)}
              </div>
            )
          }

          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelectVariant(key)}
              className={[
                'rounded-lg border px-4 py-3 text-left transition-colors',
                selected
                  ? 'border-slate-950 bg-slate-50 shadow-sm'
                  : 'border-slate-200 bg-white hover:border-slate-400',
              ].join(' ')}
              aria-pressed={selected}
            >
              {renderVariantContent(variant, index)}
            </button>
          )
        })}
      </div>
    </section>
  )
}
